import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setRefetch } from '../../redux/slices/menu';
import paymentService from '../../services/payment';
import Loading from '../../components/loading';
import LanguageList from '../../components/language-list';
import getTranslationFields from '../../helpers/getTranslationFields';
import getLanguageFields from '../../helpers/getLanguageFields';

export default function PaymentEditModal({ modal, handleCancel }) {
  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);
  const { languages, defaultLang } = useSelector(
    (state) => state.formLang,
    shallowEqual
  );
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [thumbnail, setThumbnail] = useState({ file: null, preview: null });

  const handleOk = () => {
    console.log('ok');
  };

  const handleThumbnailChange = (info) => {
    const file = info?.file?.originFileObj || info?.file;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnail({ file, preview: e?.target?.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThumbnailRemove = () => {
    setThumbnail({ file: null, preview: null });
  };

  function fetchPayment(id) {
    setLoading(true);
    paymentService
      .getById(id)
      .then(({ data }) => {
        const fields = ['title', 'client_title', 'secret_title'];
        const translations = getLanguageFields(languages, data, fields);
        form.setFieldsValue({ ...data, ...translations });
        // Set existing thumbnail preview
        if (data?.thumbnail) {
          setThumbnail({ file: null, preview: data.thumbnail });
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (modal) {
      fetchPayment(modal.id);
    } else {
      // Reset thumbnail when modal closes
      setThumbnail({ file: null, preview: null });
    }
  }, [modal]);

  const onFinish = (values) => {
    const formData = new FormData();
    formData.append('client_id', values.client_id || '');
    formData.append('secret_id', values.secret_id || '');
    formData.append('fees', values.fees ?? 0);
    
    // Append translation fields
    const titleFields = getTranslationFields(languages, values, 'title');
    const clientTitleFields = getTranslationFields(languages, values, 'client_title');
    const secretTitleFields = getTranslationFields(languages, values, 'secret_title');
    
    Object.entries(titleFields).forEach(([key, value]) => {
      formData.append(`title[${key}]`, value || '');
    });
    Object.entries(clientTitleFields).forEach(([key, value]) => {
      formData.append(`client_title[${key}]`, value || '');
    });
    Object.entries(secretTitleFields).forEach(([key, value]) => {
      formData.append(`secret_title[${key}]`, value || '');
    });
    
    // Append thumbnail if a new file is selected
    if (thumbnail?.file) {
      formData.append('thumbnail', thumbnail.file);
    }
    
    // For PUT requests via POST (Laravel method spoofing)
    formData.append('_method', 'PUT');
    
    setLoadingBtn(true);
    paymentService
      .updateWithFile(modal.id, formData)
      .then(() => {
        handleCancel();
        dispatch(setRefetch(activeMenu));
      })
      .finally(() => setLoadingBtn(false));
  };

  return (
    <Modal
      visible={!!modal}
      title={t('edit.payment')}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Button
          type='primary'
          onClick={() => form.submit()}
          loading={loadingBtn}
          key='save-btn'
        >
          {t('save')}
        </Button>,
        <Button type='default' onClick={handleCancel} key='cancel-btn'>
          {t('cancel')}
        </Button>,
      ]}
    >
      {!loading ? (
        <>
          <div className='d-flex justify-content-end'>
            <LanguageList />
          </div>
          <Form form={form} layout='vertical' onFinish={onFinish}>
            <Row gutter={12}>
              <Col span={24}>
                {languages.map((item) => (
                  <Form.Item
                    label={t('title')}
                    name={`title[${item.locale}]`}
                    rules={[
                      {
                        required: item.locale === defaultLang,
                        message: t('required'),
                      },
                    ]}
                    hidden={item.locale !== defaultLang}
                  >
                    <Input />
                  </Form.Item>
                ))}
              </Col>
              <Col span={24}>
                {languages.map((item) => (
                  <Form.Item
                    label={t('client.title')}
                    name={`client_title[${item.locale}]`}
                    rules={[
                      {
                        required: item.locale === defaultLang,
                        message: t('required'),
                      },
                    ]}
                    hidden={item.locale !== defaultLang}
                  >
                    <Input />
                  </Form.Item>
                ))}
              </Col>
              <Col span={24}>
                {languages.map((item) => (
                  <Form.Item
                    label={t('secret.title')}
                    name={`secret_title[${item.locale}]`}
                    rules={[
                      {
                        required: item.locale === defaultLang,
                        message: t('requried'),
                      },
                    ]}
                    hidden={item.locale !== defaultLang}
                  >
                    <Input />
                  </Form.Item>
                ))}
              </Col>
              <Col span={24}>
                <Form.Item
                  label={t('client.id')}
                  name='client_id'
                  rules={[
                    {
                      required: true,
                      message: t('required'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={t('secret.id')}
                  name='secret_id'
                  rules={[
                    {
                      required: true,
                      message: t('required'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={t('payment.fees')}
                  name='fees'
                  rules={[
                    {
                      type: 'number',
                      min: 0,
                      max: 100,
                      message: t('value.must.be.between.0.and.100'),
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={t('thumbnail')} name='thumbnail'>
                  <Upload
                    name='thumbnail'
                    listType='picture-card'
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleThumbnailChange}
                    accept='image/*'
                  >
                    {thumbnail?.preview ? (
                      <img
                        src={thumbnail.preview}
                        alt='thumbnail'
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>{t('upload')}</div>
                      </div>
                    )}
                  </Upload>
                  {thumbnail?.preview && (
                    <Button
                      type='link'
                      danger
                      onClick={handleThumbnailRemove}
                      style={{ padding: 0, marginTop: 8 }}
                    >
                      {t('remove')}
                    </Button>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </>
      ) : (
        <Loading />
      )}
    </Modal>
  );
}
